import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, Loader2, RotateCcw, Sparkles, XCircle } from "lucide-react";
import { toast } from "sonner";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { cn } from "@/platform/lib/cn";
import { useSession } from "@/platform/auth/use-session";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";
import {
  gradeUspReview,
  loadDueUspReviews,
  type UspReviewQuestion,
} from "@/tenants/usp/features/review/api";

const ALT_LETTERS = ["A", "B", "C", "D", "E"] as const;

export function UspRevisarPage() {
  const { session } = useSession();
  const [idx, setIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [done, setDone] = useState(0);

  const query = useQuery({
    queryKey: ["usp", "reviews", "due"],
    queryFn: () => loadDueUspReviews(session!.user.id),
    enabled: !!session,
  });

  const reviews = query.data ?? [];
  const current = reviews[idx];

  async function grade(review: UspReviewQuestion, quality: number) {
    try {
      await gradeUspReview(review.id, quality, {
        ease_factor: review.ease_factor,
        interval_days: review.interval_days,
        repetitions: review.repetitions,
      });
      setDone((d) => d + 1);
      if (idx < reviews.length - 1) {
        setIdx((i) => i + 1);
        setRevealed(false);
        setSelected(null);
      } else {
        setIdx(reviews.length); // finish
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao salvar.");
    }
  }

  if (query.isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="size-8 animate-spin text-[color:var(--color-muted-foreground)]" />
      </div>
    );
  }

  if (reviews.length === 0 || idx >= reviews.length) {
    return (
      <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
        <Blob color="sky" className="pointer-events-none absolute -left-32 -top-20 w-96 opacity-25 blur-2xl animate-blob" />
        <Container size="default" className="relative py-20 text-center">
          <Badge className="gap-1.5" style={{ backgroundColor: "var(--color-success)", color: "white", border: "none" }}>
            <CheckCircle2 className="size-3" />
            {done > 0 ? "Revisão concluída" : "Tudo em dia"}
          </Badge>
          <h1 className="mt-6 font-display text-[length:var(--text-display-md)] font-semibold leading-tight">
            {done > 0 ? `${done} questões revisadas.` : "Nenhuma revisão pendente."}
          </h1>
          <p className="mt-3 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)]">
            {done > 0
              ? "As questões voltam no momento certo da curva de esquecimento."
              : "Faça um simulado da 1ª fase — questões erradas entram na fila de revisão."}
          </p>
          <div className="mt-8 flex justify-center gap-3">
            <Button asChild variant="primary">
              <Link to="/usp/painel">Voltar ao painel</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/usp/1afase">Fazer simulado</Link>
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  if (!current) return null;
  const correctLetter = current.question.correct_answer;

  return (
    <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
      <Blob color="sky" className="pointer-events-none absolute -left-32 -top-20 w-96 opacity-20 blur-2xl animate-blob" />
      <SoftStar color="mint" className="pointer-events-none absolute right-12 top-32 w-12 opacity-50 animate-float" />

      <Container size="default" className="relative py-12 md:py-16">
        <div className="flex items-center justify-between">
          <Link to="/usp/painel" className="inline-flex items-center gap-1.5 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] hover:text-[color:var(--color-foreground)]">
            <ArrowLeft className="size-3.5" />
            Painel
          </Link>
          <Badge variant="accentSolid" className="gap-1.5">
            <RotateCcw className="size-3" />
            {idx + 1} / {reviews.length}
          </Badge>
        </div>

        <article className="mt-8 rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7 md:p-9 shadow-[var(--shadow-sm)]">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="capitalize">{current.question.discipline}</Badge>
            <span className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
              Fuvest {current.question.year} · Q{current.question.number}
            </span>
          </div>

          <p className="mt-5 whitespace-pre-line text-[length:var(--text-body)] leading-relaxed">
            {current.question.stem}
          </p>

          {current.question.alternatives && (
            <ul className="mt-6 space-y-2.5">
              {ALT_LETTERS.map((letter) => {
                const text = current.question.alternatives?.[letter];
                if (!text) return null;
                const isCorrect = correctLetter === letter;
                const isSelected = selected === letter;
                return (
                  <li key={letter}>
                    <button
                      type="button"
                      disabled={revealed}
                      onClick={() => setSelected(letter)}
                      className={cn(
                        "w-full text-left rounded-xl border-2 p-4 transition-colors",
                        !revealed && isSelected && "border-[color:var(--color-accent)] bg-[color:var(--color-accent-soft)]",
                        !revealed && !isSelected && "border-[--color-border] hover:border-[--color-accent]/40",
                        revealed && isCorrect && "border-[color:var(--color-success)] bg-[color:var(--color-success)]/10",
                        revealed && isSelected && !isCorrect && "border-[color:var(--color-danger)] bg-[color:var(--color-danger)]/10",
                        revealed && !isCorrect && !isSelected && "opacity-60 border-[--color-border]",
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full bg-[color:var(--color-muted)] font-mono text-[length:var(--text-caption)] font-bold">
                          {letter}
                        </span>
                        <span className="flex-1 text-[length:var(--text-body-sm)]">{text}</span>
                        {revealed && isCorrect && <CheckCircle2 className="size-5 shrink-0" style={{ color: "var(--color-success)" }} />}
                        {revealed && isSelected && !isCorrect && <XCircle className="size-5 shrink-0" style={{ color: "var(--color-danger)" }} />}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {!revealed ? (
            <Button className="mt-6 w-full" size="lg" variant="primary" onClick={() => setRevealed(true)} disabled={!selected}>
              <Sparkles className="size-4" />
              Revelar resposta
            </Button>
          ) : (
            <div className="mt-6">
              <p className="text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] mb-3">
                Como foi lembrar dessa questão?
              </p>
              <div className="grid grid-cols-3 gap-2">
                <Button variant="outline" onClick={() => grade(current, 1)} className="!border-[color:var(--color-danger)]/40">
                  Errei
                </Button>
                <Button variant="outline" onClick={() => grade(current, 3)} className="!border-[color:var(--color-warning)]/40">
                  Com esforço
                </Button>
                <Button variant="outline" onClick={() => grade(current, 5)} className="!border-[color:var(--color-success)]/40">
                  Fácil
                </Button>
              </div>
            </div>
          )}
        </article>
      </Container>
    </div>
  );
}
